import { http } from '@/shared/api/axios'
import {
  BroadcastDetailSchema,
  BroadcastReachSchema,
  BroadcastSchema,
  ChatConversationDetailSchema,
  ChatConversationSchema,
  ChatDownloadUrlsSchema,
  ChatMessageSchema,
  ChatUploadSchema,
  DirectoryEntrySchema,
  type AddGroupMembersRequest,
  type Broadcast,
  type BroadcastDetail,
  type BroadcastReach,
  type ChatConversation,
  type ChatConversationDetail,
  type ChatMessage,
  type ChatUpload,
  type CreateGroupRequest,
  type DeliveredItem,
  type DirectoryEntry,
  type SendMessageRequest,
  BroadcastShiftSchema,
  type BroadcastShift,
} from './types'
import { z } from 'zod'

const BASE = '/v1/messaging'
const quiet = { _skipErrorToast: true } as never

export const MESSAGE_PAGE_LIMIT = 35

export const chatApi = {
  conversations: async (): Promise<ChatConversation[]> => {
    const { data } = await http.get(`${BASE}/conversations`, quiet)
    return z.array(ChatConversationSchema).catch([]).parse(data)
  },

  conversation: async (id: number): Promise<ChatConversationDetail> => {
    const { data } = await http.get(`${BASE}/conversations/${id}`, quiet)
    return ChatConversationDetailSchema.parse(data)
  },

  messages: async (id: number, params: { before?: number; after?: number }): Promise<ChatMessage[]> => {
    const { data } = await http.get(`${BASE}/conversations/${id}/messages`, {
      params: { ...params, limit: MESSAGE_PAGE_LIMIT },
      _skipErrorToast: true,
    } as never)
    return z.array(ChatMessageSchema).catch([]).parse(data)
  },

  startClientConversation: async (clientId: number): Promise<ChatConversation> => {
    const { data } = await http.post(`${BASE}/conversations/client-ops/${clientId}`, undefined, quiet)
    return ChatConversationSchema.parse(data)
  },

  messagesByIds: async (id: number, ids: number[]): Promise<ChatMessage[]> => {
    const { data } = await http.post(`${BASE}/conversations/${id}/messages/by-ids`, { ids }, quiet)
    return z.array(ChatMessageSchema).catch([]).parse(data)
  },

  send: async (id: number, request: SendMessageRequest): Promise<ChatMessage> => {
    const { data } = await http.post(`${BASE}/conversations/${id}/messages`, request, quiet)
    return ChatMessageSchema.parse(data)
  },

  deleteMessage: async (messageId: number): Promise<void> => {
    await http.delete(`${BASE}/messages/${messageId}`, quiet)
  },

  markRead: async (id: number, lastReadMessageId: number): Promise<void> => {
    await http.put(`${BASE}/conversations/${id}/read`, { lastReadMessageId }, quiet)
  },

  markDelivered: async (items: DeliveredItem[]): Promise<void> => {
    await http.put(`${BASE}/conversations/delivered`, { items }, quiet)
  },

  setMuted: async (id: number, muted: boolean): Promise<void> => {
    await http.put(`${BASE}/conversations/${id}/mute`, { muted }, quiet)
  },

  upload: async (
    id: number,
    file: File,
    contentType: string,
    dimensions: { width?: number; height?: number },
  ): Promise<ChatUpload> => {
    const form = new FormData()
    form.append('file', new File([file], file.name, { type: contentType }))
    if (dimensions.width != null) form.append('width', String(dimensions.width))
    if (dimensions.height != null) form.append('height', String(dimensions.height))
    const { data } = await http.post(`${BASE}/conversations/${id}/attachments`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      _skipErrorToast: true,
    } as never)
    return ChatUploadSchema.parse(data)
  },

  attachmentUrls: async (id: number, fileIds: string[]): Promise<Record<string, string>> => {
    const { data } = await http.post(`${BASE}/conversations/${id}/attachments/urls`, { fileIds }, quiet)
    return ChatDownloadUrlsSchema.parse(data).urls
  },

  broadcasts: async (clientId: number | null): Promise<Broadcast[]> => {
    const { data } = await http.get(`${BASE}/broadcasts`, {
      params: clientId === null ? undefined : { clientId },
      _skipErrorToast: true,
    } as never)
    return z.array(BroadcastSchema).catch([]).parse(data)
  },

  broadcast: async (id: number): Promise<BroadcastDetail> => {
    const { data } = await http.get(`${BASE}/broadcasts/${id}`, quiet)
    return BroadcastDetailSchema.parse(data)
  },

  broadcastReach: async (clientId: number): Promise<BroadcastReach> => {
    const { data } = await http.get(`${BASE}/broadcasts/reach`, {
      params: { audienceType: 'client', audienceId: clientId },
      _skipErrorToast: true,
    } as never)
    return BroadcastReachSchema.parse(data)
  },

  broadcastShifts: async (): Promise<BroadcastShift[]> => {
    const { data } = await http.get(`${BASE}/broadcasts/shifts`, quiet)
    return z.array(BroadcastShiftSchema).parse(data)
  },

  shiftBroadcastReach: async (shiftId: number, guardUserIds: number[] | null): Promise<BroadcastReach> => {
    const { data } = await http.get(`${BASE}/broadcasts/reach`, {
      params: { audienceType: 'shift', audienceId: shiftId, ...(guardUserIds ? { guardUserIds } : {}) },
      paramsSerializer: { indexes: null },
      _skipErrorToast: true,
    } as never)
    return BroadcastReachSchema.parse(data)
  },

  sendShiftBroadcast: async (shiftId: number, body: string, guardUserIds: number[] | null): Promise<Broadcast> => {
    const { data } = await http.post(
      `${BASE}/broadcasts`,
      { audienceType: 'shift', audienceId: shiftId, body, guardUserIds },
      quiet,
    )
    return BroadcastSchema.parse(data)
  },

  sendBroadcast: async (clientId: number, body: string): Promise<Broadcast> => {
    const { data } = await http.post(
      `${BASE}/broadcasts`,
      { audienceType: 'client', audienceId: clientId, body },
      quiet,
    )
    return BroadcastSchema.parse(data)
  },

  directory: async (search: string, limit = 20): Promise<DirectoryEntry[]> => {
    const { data } = await http.get(`${BASE}/directory`, {
      params: { search: search || undefined, limit },
      _skipErrorToast: true,
    } as never)
    return z.array(DirectoryEntrySchema).catch([]).parse(data)
  },

  createDirect: async (otherUserId: number): Promise<ChatConversation> => {
    const { data } = await http.post(`${BASE}/conversations/direct/${otherUserId}`, undefined, quiet)
    return ChatConversationSchema.parse(data)
  },

  createGroup: async (request: CreateGroupRequest): Promise<ChatConversation> => {
    const { data } = await http.post(`${BASE}/conversations/group`, request, quiet)
    return ChatConversationSchema.parse(data)
  },

  addGroupMembers: async (id: number, request: AddGroupMembersRequest): Promise<void> => {
    await http.post(`${BASE}/conversations/${id}/members`, request, quiet)
  },

  removeGroupMember: async (id: number, userId: number): Promise<void> => {
    await http.delete(`${BASE}/conversations/${id}/members/${userId}`, quiet)
  },

  leaveGroup: async (id: number): Promise<void> => {
    await http.post(`${BASE}/conversations/${id}/leave`, undefined, quiet)
  },

  renameGroup: async (id: number, title: string): Promise<void> => {
    await http.patch(`${BASE}/conversations/${id}/title`, { title }, quiet)
  },
}
